import React, { useState, useEffect } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, Search, MapPin, Phone, Mail, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const AgentDirectoryPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterProvince, setFilterProvince] = useState('');

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCountry) params.append('country', filterCountry);
      if (filterProvince) params.append('province', filterProvince);

      const response = await fetch(`${API_URL}/api/agents/directory?${params.toString()}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Failed to fetch agent directory:', error);
      toast.error('Failed to load agent directory');
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      agent.name?.toLowerCase().includes(search) ||
      agent.business_name?.toLowerCase().includes(search) ||
      agent.city?.toLowerCase().includes(search) ||
      agent.province?.toLowerCase().includes(search)
    );
  });

  return (
    <AgentLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8" />
            Agent Network Directory
          </h1>
          <p className="text-muted-foreground mt-1">
            Connect with other agents in the Matrise network
          </p>
        </div>

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search agents..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Input
                placeholder="Filter by country"
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
              />
              <Input
                placeholder="Filter by province"
                value={filterProvince}
                onChange={(e) => setFilterProvince(e.target.value)}
              />
            </div>
            <Button onClick={fetchAgents} className="mt-4">
              Apply Filters
            </Button>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{filteredAgents.length}</div>
              <p className="text-xs text-muted-foreground mt-2">In the network</p>
            </CardContent>
          </Card>
        </div>

        {/* Agents Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredAgents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-lg font-medium">No agents found</p>
              <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredAgents.map((agent, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{agent.name}</h3>
                        {agent.business_name && (
                          <p className="text-sm text-muted-foreground">{agent.business_name}</p>
                        )}
                      </div>
                      {agent.online && (
                        <Badge className="bg-green-500">Online</Badge>
                      )}
                    </div>

                    {agent.city && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {agent.city}{agent.province && `, ${agent.province}`}
                      </div>
                    )}

                    {agent.services && agent.services.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {agent.services.map((service, sidx) => (
                          <Badge key={sidx} variant="outline" className="text-xs">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <Button
                      className="w-full mt-4"
                      onClick={() => navigate('/agent/transfer', { state: { toAgentId: agent.user_id, toAgentName: agent.name } })}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send Float
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AgentLayout>
  );
};